/**
 * My Policies — staff-facing policy library with per-user ack state.
 *
 * Team members land here to see every firm policy and their own acknowledgement
 * status against each: current (green), needs re-ack after version bump
 * (amber), or never acknowledged (red). Rows link through to the reader page
 * where the ack button lives (see PolicyReader.jsx).
 *
 * Backed by GET /api/policy/my/library. Session-authed, no role gate.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authFetch } from "../../config/api";
import {
  BookOpen, CheckCircle2, AlertTriangle, Circle,
  AlertCircle, RefreshCw,
} from "lucide-react";
import TierBadge from "./TierBadge";

const API = process.env.REACT_APP_BACKEND_URL || "";

function AckBadge({ status, attestedVersion, attestedAt }) {
  if (status === "current") {
    return (
      <span
        data-testid={`ack-status-current`}
        className="inline-flex items-center gap-1 text-emerald-700 text-xs"
        title={`Acknowledged ${attestedVersion || ""}${attestedAt ? " on " + new Date(attestedAt).toLocaleDateString() : ""}`}
      >
        <CheckCircle2 size={14} />
        Acknowledged
      </span>
    );
  }
  if (status === "outdated") {
    return (
      <span
        data-testid={`ack-status-outdated`}
        className="inline-flex items-center gap-1 text-amber-700 text-xs"
        title={`Last acknowledged ${attestedVersion || ""} \u2014 policy has been updated, please re-read and re-acknowledge`}
      >
        <AlertTriangle size={14} />
        Needs re-ack
      </span>
    );
  }
  return (
    <span
      data-testid={`ack-status-never`}
      className="inline-flex items-center gap-1 text-rose-700 text-xs"
    >
      <Circle size={14} />
      Not yet acknowledged
    </span>
  );
}

export default function MyPolicies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(`${API}/api/policy/my/library`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="my-policies-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading your policies...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="my-policies-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load: {error}
      </div>
    );
  }
  if (!data) return null;

  const { policies = [], needs_ack_count = 0 } = data;

  return (
    <div data-testid="my-policies-page" className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <BookOpen size={22} />
          <h1 className="text-2xl font-semibold text-slate-900">My Policies</h1>
        </div>
        <p className="mt-2 text-sm text-slate-600 max-w-3xl">
          Read each firm policy and acknowledge that you have read and understood
          it. When a policy is updated, your previous acknowledgement is
          invalidated and you will need to re-read and re-acknowledge the new
          version.
        </p>
        {needs_ack_count > 0 && (
          <div
            data-testid="my-policies-summary"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-amber-50 px-3 py-1.5 text-sm text-amber-900 border border-amber-200"
          >
            <AlertTriangle size={16} />
            {needs_ack_count} {needs_ack_count === 1 ? "policy needs" : "policies need"} your acknowledgement
          </div>
        )}
        {needs_ack_count === 0 && policies.length > 0 && (
          <div
            data-testid="my-policies-summary-ok"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-sm text-emerald-900 border border-emerald-200"
          >
            <CheckCircle2 size={16} />
            You are up to date on all firm policies
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Policy</th>
              <th className="text-left px-4 py-2 font-medium">Tier</th>
              <th className="text-left px-4 py-2 font-medium">Current version</th>
              <th className="text-left px-4 py-2 font-medium">My status</th>
              <th className="text-right px-4 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr
                key={p.slug}
                data-testid={`my-policies-row-${p.slug}`}
                className="border-t border-slate-100"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <TierBadge tier={p.tier} testId={`my-policies-tier-${p.slug}`} />
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {p.current_version || "\u2014"}
                </td>
                <td className="px-4 py-3">
                  <AckBadge
                    status={p.ack_status}
                    attestedVersion={p.attested_version_label}
                    attestedAt={p.attested_at}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    data-testid={`my-policies-read-${p.slug}`}
                    to={`/policy/reader/${encodeURIComponent(p.slug)}`}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                      p.needs_ack
                        ? "bg-slate-900 text-white hover:bg-slate-800"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {p.needs_ack ? "Read & acknowledge" : "Read"}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
